import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';

import { Message, MessageService } from '@rsm/shared/message';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private messageService: MessageService) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldNotify(error)) {
          this.messageService.addMsgDanger(this.buildMessage(error));
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * 401 é silencioso (tratado pelo fluxo de sessão/redirect). 403 só notifica
   * quando o back-end envia uma mensagem de negócio no corpo (ex.: regra do
   * Anjo) — 403 sem corpo útil (token/permissão genérica) permanece silencioso.
   * Demais status seguem o comportamento padrão de notificar.
   */
  private shouldNotify(error: HttpErrorResponse): boolean {
    if (error.status === 401) {
      return false;
    }
    if (error.status === 403) {
      const body = error.error;
      return !!(body && typeof body === 'object' && (body.error || body.message || body.mensagem));
    }
    return true;
  }

  private buildMessage(error: HttpErrorResponse): Message {
    const body = error.error || {};
    const bodyMessage: string | null =
      typeof body === 'object' ? (body.message || body.mensagem || body.error || this.buildAttributesMessage(body)) : null;
    const rawParams = typeof body === 'object' ? (body.parameters || body.parametros) : null;
    const parameters: string[] = Array.isArray(rawParams)
      ? rawParams.map((item: any) => `${item ?? ''}`.trim()).filter(Boolean)
      : [];
    const formattedMessage =
      bodyMessage && parameters.length
        ? (parameters.some((p) => bodyMessage.includes(p)) ? bodyMessage : `${bodyMessage}: ${parameters.join(', ')}`)
        : bodyMessage;

    switch (error.status) {
      case 0:
        return {
          header: 'Sem Conexão',
          message: 'Não foi possível conectar ao servidor. Verifique sua internet.',
        };
      case 400:
        return {
          header: 'Dados Inválidos',
          message: formattedMessage || 'Verifique os campos preenchidos e tente novamente.',
        };
      case 403:
        return {
          header: 'Acesso Negado',
          message: formattedMessage || 'Você não tem permissão para executar esta ação.',
        };
      case 404:
        return {
          header: 'Não Encontrado',
          message: formattedMessage || 'O recurso solicitado não foi encontrado.',
        };
      case 409:
        return {
          header: 'Conflito',
          message: formattedMessage || 'Este registro já existe ou há um conflito de dados.',
        };
      case 422:
        return {
          header: 'Erro de Validação',
          message: formattedMessage || 'Verifique os dados preenchidos e tente novamente.',
        };
      case 500:
        return {
          header: 'Erro no Servidor',
          message: formattedMessage || 'Ocorreu um erro interno. Tente novamente em instantes.',
        };
      default:
        return {
          header: 'Erro',
          message: formattedMessage || 'Ops! Ocorreu um erro inesperado, tente novamente.',
        };
    }
  }

  private buildAttributesMessage(body: any): string | null {
    if (!Array.isArray(body?.attributes) || body.attributes.length === 0) {
      return null;
    }
    return body.attributes
      .filter((a: any) => a?.description)
      .map((a: any) => a.description)
      .join('\n') || null;
  }
}
